import { Link, useLocation, useNavigate } from 'react-router';
import { useLogout, useMe } from '../api/auth';
import { EventSwitcher } from './EventSwitcher';
import { I } from './Icons';

export interface SidebarProps {
  /**
   * The current event in the URL. When omitted (e.g. on /users, /me, the
   * Stripe Accounts page), the event-scoped section of the sidebar is hidden
   * but the tenant-scoped section (Users, My account, Logout) still renders.
   */
  eventId?: string;
  open: boolean;
  onClose: () => void;
}

interface EventNavLink {
  label: string;
  to: (eventId: string) => string;
  match: (path: string, eventId: string) => boolean;
  icon: typeof I.settings;
}

const EVENT_NAV: EventNavLink[] = [
  {
    label: 'Sales',
    to: (eventId) => `/events/${eventId}/sales`,
    match: (path, eventId) => path.startsWith(`/events/${eventId}/sales`),
    icon: I.dollar,
  },
  {
    label: 'Catalog',
    to: (eventId) => `/events/${eventId}/catalog`,
    match: (path, eventId) => path.startsWith(`/events/${eventId}/catalog`),
    icon: I.package,
  },
  {
    label: 'Settings',
    to: (eventId) => `/events/${eventId}/setup`,
    match: (path, eventId) => path.startsWith(`/events/${eventId}/setup`),
    icon: I.settings,
  },
];

interface TenantNavLink {
  label: string;
  to: string;
  match: (path: string) => boolean;
  icon: typeof I.settings;
  ownerOnly?: boolean;
}

const TENANT_NAV: TenantNavLink[] = [
  {
    label: 'Users',
    to: '/users',
    match: (path) => path.startsWith('/users'),
    icon: I.users,
    ownerOnly: true,
  },
  {
    label: 'Stripe accounts',
    to: '/stripe-accounts',
    match: (path) => path.startsWith('/stripe-accounts'),
    icon: I.card,
    ownerOnly: true,
  },
  {
    // Owner + manager: no ownerOnly flag.
    label: 'API keys',
    to: '/api-keys',
    match: (path) => path.startsWith('/api-keys'),
    icon: I.lock,
  },
  {
    label: 'My account',
    to: '/me',
    match: (path) => path.startsWith('/me'),
    icon: I.user,
  },
];

export function Sidebar({ eventId, open, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const me = useMe();
  const role = me.data?.role;

  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login'),
    });
  }

  return (
    <aside className={`sidebar${open ? ' open' : ''}`}>
      <div className="brand">
        <span className="product">InkyPay</span>
        <button
          type="button"
          className="mobile-close"
          onClick={onClose}
          aria-label="Close menu"
        >
          <I.x size={18} />
        </button>
      </div>

      {eventId && (
        <>
          <div className="sidebar-event">
            <EventSwitcher />
          </div>
          <nav className="nav-group">
            {EVENT_NAV.map((item) => {
              const Icon = item.icon;
              const active = item.match(location.pathname, eventId);
              return (
                <Link
                  key={item.label}
                  to={item.to(eventId)}
                  className={`nav-item${active ? ' active' : ''}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </>
      )}

      <nav className="nav-group">
        <Link
          to="/events"
          className={`nav-item${location.pathname === '/events' ? ' active' : ''}`}
        >
          <I.calendar size={18} />
          <span>Events</span>
        </Link>
        {TENANT_NAV.filter((item) => !item.ownerOnly || role === 'owner').map((item) => {
          const Icon = item.icon;
          const active = item.match(location.pathname);
          return (
            <Link
              key={item.label}
              to={item.to}
              className={`nav-item${active ? ' active' : ''}`}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button
          type="button"
          className="nav-item"
          onClick={handleLogout}
          disabled={logout.isPending}
        >
          <I.logout size={18} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}
